import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../../../services/categoryService';
import { locationService } from '../../../services/locationService';
import { routeService } from '../../../services/routeService';
import './AdminCategories.css';

const AdminCategoryAdd = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Локации',
    assignedItems: []
  });
  
  const [availableItems, setAvailableItems] = useState({
    locations: [],
    routes: []
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());

  useEffect(() => {
    setAssignedIds(new Set(formData.assignedItems.map(item => item.id)));
  }, [formData.assignedItems]);

  useEffect(() => {
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const [locations, routes] = await Promise.all([
        locationService.getAllLocations(),
        routeService.getAllRoutes()
      ]);
      
      setAvailableItems({
        locations: Array.isArray(locations) ? locations : [],
        routes: Array.isArray(routes) ? routes : []
      });
    } catch (error) {
      console.error('Ошибка загрузки элементов:', error);
      setError('Не удалось загрузить список локаций и маршрутов');
      setAvailableItems({
        locations: [],
        routes: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      assignedItems: name === 'type' ? [] : prev.assignedItems
    }));
  };

  const handleAddItem = (item) => {
    setFormData(prev => ({
      ...prev,
      assignedItems: [...prev.assignedItems, item]
    }));
  };

  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      assignedItems: prev.assignedItems.filter(item => item.id !== itemId)
    }));
  };

  const getUnassignedItems = () => {
    const items = formData.type === 'Локации' 
      ? (availableItems.locations || [])
      : (availableItems.routes || []);
    
    return items.filter(item => !assignedIds.has(item.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название категории');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      console.log('1. Отправляем данные для создания категории:', {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        type: formData.type
      });
      
      // 1. Создаем категорию (возвращается Category entity напрямую)
      const newCategory = await categoryService.createCategory(
        formData.name.trim(),
        formData.description.trim() || null,
        formData.type === 'Локации' ? 'location' : 'route'
      );
      
      console.log('2. Созданная категория:', newCategory);
      
      const categoryId = newCategory.id;
      
      if (!categoryId) {
        throw new Error('Не удалось получить ID созданной категории');
      }
      
      // 2. Привязываем элементы к категории
      if (formData.assignedItems.length > 0) {
        console.log(`3. Привязываем ${formData.assignedItems.length} элементов к категории ${categoryId}`);
        const itemIds = formData.assignedItems.map(item => item.id);
        
        if (formData.type === 'Локации') {
          await categoryService.updateCategoryLocations(categoryId, itemIds);
        } else {
          await categoryService.updateCategoryRoutes(categoryId, itemIds);
        }
        
        console.log('4. Элементы успешно привязаны');
      }
      
      alert('Категория успешно создана');
      navigate('/admin/categories/list');
      
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setError(error.message || 'Не удалось создать категорию');
    } finally {
      setSubmitting(false);
    }
  };

  const currentItems = formData.assignedItems || [];
  const unassignedItems = getUnassignedItems();

  if (loading && !availableItems.locations.length && !availableItems.routes.length) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className="admin-category-form">
      <h2 className="admin-category-form__title">Добавление категории</h2>

      {error && (
        <div className="admin-category-form__error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-category-form__form">
        {/* Основная информация */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">Основная информация</h3>
          
          <div className="admin-category-form__field">
            <label htmlFor="name">
              Название категории <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название категории"
              autoFocus
            />
          </div>

          <div className="admin-category-form__field">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              disabled={submitting}
              placeholder="Введите описание категории"
            />
          </div>

          <div className="admin-category-form__field">
            <label>Тип категории <span className="required">*</span></label>
            <div className="admin-category-form__type-selector">
              <label className={`type-option ${formData.type === 'Локации' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="Локации"
                  checked={formData.type === 'Локации'}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <span className="type-icon">📍</span>
                <span className="type-label">Локации</span>
              </label>
              <label className={`type-option ${formData.type === 'Маршруты' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="Маршруты"
                  checked={formData.type === 'Маршруты'}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <span className="type-icon">🗺️</span>
                <span className="type-label">Маршруты</span>
              </label>
            </div>
          </div>
        </div>

        {/* Управление элементами */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">
            Управление {formData.type === 'Локации' ? 'локациями' : 'маршрутами'}
          </h3>
          
          <div className="admin-category-form__items-management">
            {/* Левый список: элементы в категории */}
            <div className="items-list items-list--assigned">
              <div className="items-list__header">
                <h4>
                  {formData.type === 'Локации' ? 'Локации' : 'Маршруты'} в категории
                  <span className="items-count">({currentItems.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {currentItems.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">📭</span>
                    <p>Нет добавленных элементов</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {currentItems.map(item => (
                      <div key={item.id} className="item-card">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="item-card__remove"
                          disabled={submitting}
                          title="Удалить из категории"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Правый список: доступные элементы */}
            <div className="items-list items-list--available">
              <div className="items-list__header">
                <h4>
                  Доступные {formData.type === 'Локации' ? 'локации' : 'маршруты'}
                  <span className="items-count">({unassignedItems.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {unassignedItems.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">✅</span>
                    <p>Все элементы уже добавлены</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {unassignedItems.map(item => (
                      <div key={item.id} className="item-card item-card--available">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddItem(item)}
                          className="item-card__add"
                          disabled={submitting}
                          title="Добавить в категорию"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="admin-category-form__actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? (
              <>
                <span className="btn-spinner"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить категорию'
            )}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/categories/list')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCategoryAdd;